import React, { useEffect, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from '@mui/material'
import type { Auction } from '@otw/models'

export interface AuctionDialogProps {
  open: boolean
  onClose: () => void
  auction?: Auction | null
  onCreate: (input: { name: string; gate_passes_needed: boolean }) => Promise<void>
  onUpdate: (auction: Auction) => Promise<void>
  onError?: (message: string) => void
}

const AuctionDialog: React.FC<AuctionDialogProps> = ({
  open,
  onClose,
  auction = null,
  onCreate,
  onUpdate,
  onError,
}) => {
  const isEditing = Boolean(auction?.id)
  const [name, setName] = useState('')
  const [gatePassesNeeded, setGatePassesNeeded] = useState(true)
  const [saving, setSaving] = useState(false)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setName(auction?.name ?? '')
    setGatePassesNeeded(auction?.gate_passes_needed !== false)
    setTouched(false)
    setSaving(false)
  }, [open, auction])

  const valid = name.trim().length > 0

  const handleSubmit = async () => {
    setTouched(true)
    if (!valid) return
    setSaving(true)
    try {
      if (isEditing && auction?.id) {
        await onUpdate({
          ...auction,
          name: name.trim(),
          gate_passes_needed: gatePassesNeeded,
        })
      } else {
        await onCreate({
          name: name.trim(),
          gate_passes_needed: gatePassesNeeded,
        })
      }
      onClose()
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Could not save auction')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Edit auction' : 'Add auction'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label="Auction name"
            required
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            autoFocus
            error={touched && !name.trim()}

          />
          <FormControlLabel
            control={
              <Switch
                checked={gatePassesNeeded}
                onChange={(e) => setGatePassesNeeded(e.target.checked)}
                disabled={saving}
              />
            }
            label="Gate passes needed"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving || (touched && !valid)}>
          {saving ? 'Saving…' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AuctionDialog
